import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Duties from "@/pages/Duties";
import DutyForm from "@/pages/DutyForm";
import Companies from "@/pages/Companies";
import Vehicles from "@/pages/Vehicles";
import Owners from "@/pages/Owners";
import Invoices from "@/pages/Invoices";
import InvoiceNew from "@/pages/InvoiceNew";
import Quotations from "@/pages/Quotations";
import QuotationForm from "@/pages/QuotationForm";
import Settings from "@/pages/Settings";
import AIControlCenter from "@/pages/AIControlCenter";
import MarketingAgent from "@/pages/MarketingAgent";
import SocialAgent from "@/pages/SocialAgent";
import AdvisorAgent from "@/pages/AdvisorAgent";
import Drivers from "@/pages/Drivers";
import DriverDashboard from "@/pages/DriverDashboard";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen grid place-items-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === "driver") return <Navigate to="/driver" replace />;
  return user?.role === "admin" ? children : <Navigate to="/duties" replace />;
}

function NotDriver({ children }) {
  const { user } = useAuth();
  return user?.role === "driver" ? <Navigate to="/driver" replace /> : children;
}

const S = (el) => <NotDriver>{el}</NotDriver>;

function DriverOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "driver" ? children : <Navigate to="/" replace />;
}

const A = (el) => <AdminOnly>{el}</AdminOnly>;

function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            >
              <Route index element={A(<Dashboard />)} />
              <Route path="duties" element={S(<Duties />)} />
              <Route path="duties/new" element={S(<DutyForm />)} />
              <Route path="duties/:id" element={S(<DutyForm />)} />
              <Route path="companies" element={A(<Companies />)} />
              <Route path="vehicles" element={S(<Vehicles />)} />
              <Route path="owners" element={S(<Owners />)} />
              <Route path="drivers" element={A(<Drivers />)} />
              <Route path="driver" element={<DriverOnly><DriverDashboard /></DriverOnly>} />
              <Route path="invoices" element={A(<Invoices />)} />
              <Route path="invoices/new" element={A(<InvoiceNew />)} />
              <Route path="quotations" element={A(<Quotations />)} />
              <Route path="quotations/new" element={A(<QuotationForm />)} />
              <Route path="quotations/:id" element={A(<QuotationForm />)} />
              <Route path="settings" element={A(<Settings />)} />
              <Route path="ai" element={A(<AIControlCenter />)} />
              <Route path="ai/marketing" element={A(<MarketingAgent />)} />
              <Route path="ai/social" element={A(<SocialAgent />)} />
              <Route path="ai/advisor" element={A(<AdvisorAgent />)} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position={window.innerWidth < 1024 ? "top-center" : "bottom-right"} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
